import type { PlaySession } from "@slot/client-engine";
import type { GameInitialSettings } from "./GameInitialSettingsTypes";
import { BootDataError } from "../retry/BootDataError";
import { BootHttpError } from "../retry/BootHttpError";
import { BOOT_REQUEST_TIMEOUT_MS } from "../retry/BootRequestTimeout";

export class GameInitialSettingsLoader {
  /** GAME-SPECIFIC values merged over the game-agnostic fixture settings (fixture boot only). */
  private readonly fixtureOverrides: Partial<GameInitialSettings>;
  /** The boot's play session: the settings are the player's, asked for with its bearer (decision 81). */
  private readonly playSession: PlaySession | null;

  public constructor(input?: { fixtureOverrides?: Partial<GameInitialSettings>; playSession?: PlaySession | null }) {
    this.fixtureOverrides = input?.fixtureOverrides ?? {};
    this.playSession = input?.playSession ?? null;
  }

  public async loadFromUrl(url: string): Promise<GameInitialSettings> {
    if (url === "fixture") {
      const settings = { ...this.createFixtureSettings(), ...this.fixtureOverrides };
      this.validateOrThrow(settings);
      return settings;
    }

    const res = await fetch(url, { method: "GET", headers: this.playSession?.authorizationHeader() ?? {}, signal: AbortSignal.timeout(BOOT_REQUEST_TIMEOUT_MS) });
    if (!res.ok) throw await BootHttpError.of("GameInitialSettingsLoader", res);

    const json = (await res.json()) as unknown;
    if (!json || typeof json !== "object") {
      throw new BootDataError("GameInitialSettingsLoader: invalid json payload (expected object)");
    }

    const obj = json as Partial<GameInitialSettings>;
    this.validateOrThrow(obj);
    return obj as GameInitialSettings;
  }

  private createFixtureSettings(): GameInitialSettings {
    return {
      accountBalanceSnapshot: {
        currencyCode: "EUR",
        balanceAmount: "1000000",
        realBalanceAmount: null,
        bonusBalanceAmount: null,
      },
      betStepsMinor: ["10", "20", "50", "100", "200", "500", "1000", "2000", "5000"],
      initialBetMinor: "100",
      gameTitle: "Game title fetched from server",
      // Feature ids/prices are a PER-GAME server contract — supplied by the game through
      // `GameBootDefinition.fixtureSettingsOverrides`, never hardcoded here.
      featurePriceMultipliersById: {},
      featureModeMultipliersById: {},
      extraSpinsOfferFeatureIds: [],
      extraSpinsOfferCountById: {},
      gambleEnabled: true,
      autospinEnabled: true,
      autospinPresets: [10, 25, 50, 75, 100, 250],
      holdToSpinEnabled: true,
      fastSpinEnabled: true,
      superFastSpinEnabled: true,
      minAgeYears: 18,
      gameHistoryEnabled: true,
      // Big-win tier breakpoints are PER-GAME (the tier ids map to the game's title art; the
      // multipliers are game-math, and they must match the levelIdentifiers the game's scripted
      // rounds emit) — supplied for offline boot by the game through
      // `GameBootDefinition.fixtureSettingsOverrides`, never hardcoded here. In production
      // the backend sends them per game. Empty here → big wins render minimal until the game overrides.
      bigWinTiers: [],
      // No pendingRoundId: a dev boot fakes an interrupted round with `?pending=<roundId>` (`loadInitialSettings`).
      localeCode:"en"
    };
  }

  private validateOrThrow(obj: Partial<GameInitialSettings>): void {
    if (!obj.accountBalanceSnapshot) {
      throw new BootDataError("GameInitialSettingsLoader: missing accountBalanceSnapshot");
    }

    if (
      typeof obj.accountBalanceSnapshot.currencyCode !== "string" ||
      obj.accountBalanceSnapshot.currencyCode.length === 0
    ) {
      throw new BootDataError("GameInitialSettingsLoader: missing/invalid currencyCode");
    }

    if (typeof obj.accountBalanceSnapshot.balanceAmount !== "string" || !/^\d+$/.test(obj.accountBalanceSnapshot.balanceAmount)) {
      throw new BootDataError(`GameInitialSettingsLoader: invalid accountBalanceSnapshot.balanceAmount ${JSON.stringify(obj.accountBalanceSnapshot.balanceAmount)} (expected whole minor units)`);
    }

    if (!Array.isArray(obj.betStepsMinor) || obj.betStepsMinor.length === 0) {
      throw new BootDataError("GameInitialSettingsLoader: missing/invalid betStepsMinor");
    }
    for (const step of obj.betStepsMinor) {
      if (typeof step !== "string" || !/^\d+$/.test(step)) {
        throw new BootDataError(`GameInitialSettingsLoader: invalid entry ${JSON.stringify(step)} in betStepsMinor (expected whole minor units)`);
      }
    }

    if (typeof obj.initialBetMinor !== "string" || !/^\d+$/.test(obj.initialBetMinor)) {
      throw new BootDataError(`GameInitialSettingsLoader: invalid initialBetMinor ${JSON.stringify(obj.initialBetMinor)} (expected whole minor units)`);
    }

    // Bet lines (decision 56): a spin's bet is the step times the chosen count, so a count is a whole number of at least 1.
    if (obj.betLineOptions !== undefined) {
      const options: unknown = obj.betLineOptions;
      if (!Array.isArray(options) || options.length === 0 || options.some((n) => !Number.isInteger(n) || (n as number) < 1) || new Set(options).size !== options.length) {
        throw new BootDataError(`GameInitialSettingsLoader: invalid betLineOptions ${JSON.stringify(options)} (expected distinct whole numbers of at least 1)`);
      }
    }
    if (obj.initialBetLines !== undefined && !(obj.betLineOptions ?? []).includes(obj.initialBetLines)) {
      throw new BootDataError(`GameInitialSettingsLoader: initialBetLines ${JSON.stringify(obj.initialBetLines)} is not one of betLineOptions`);
    }

    if (typeof obj.gameTitle !== "string" || obj.gameTitle.trim().length === 0) {
      throw new BootDataError("GameInitialSettingsLoader: missing/invalid gameTitle");
    }

    if (obj.featurePriceMultipliersById) {
      for (const [featureId, multiplier] of Object.entries(obj.featurePriceMultipliersById)) {
        if (!featureId || typeof multiplier !== "number" || !Number.isFinite(multiplier) || multiplier <= 0) {
          throw new BootDataError("GameInitialSettingsLoader: invalid featurePriceMultipliersById");
        }
      }
    }

    if (obj.extraSpinsOfferFeatureIds) {
      if (!Array.isArray(obj.extraSpinsOfferFeatureIds)) {
        throw new BootDataError("GameInitialSettingsLoader: extraSpinsOfferFeatureIds must be an array");
      }
      for (const id of obj.extraSpinsOfferFeatureIds) {
        if (typeof id !== "string" || id.length === 0) {
          throw new BootDataError("GameInitialSettingsLoader: invalid entry in extraSpinsOfferFeatureIds");
        }
      }
    }

    if (obj.extraSpinsOfferCountById) {
      for (const [featureId, count] of Object.entries(obj.extraSpinsOfferCountById)) {
        if (!featureId || typeof count !== "number" || !Number.isFinite(count) || count <= 0 || !Number.isInteger(count)) {
          throw new BootDataError("GameInitialSettingsLoader: invalid entry in extraSpinsOfferCountById");
        }
      }
    }

    // ── Gamble settings validation ────────────────────────────

    if (obj.gambleEnabled !== undefined && typeof obj.gambleEnabled !== "boolean") {
      throw new BootDataError("GameInitialSettingsLoader: gambleEnabled must be boolean");
    }

    // ── Game history validation ──────────────────────────────

    if (obj.gameHistoryEnabled !== undefined && typeof obj.gameHistoryEnabled !== "boolean") {
      throw new BootDataError("GameInitialSettingsLoader: gameHistoryEnabled must be boolean");
    }

    // ── Pending round restore validation ────────────────────
    if (obj.pendingRoundId !== undefined && (typeof obj.pendingRoundId !== "string" || obj.pendingRoundId.length === 0)) {
      throw new BootDataError("GameInitialSettingsLoader: pendingRoundId must be a non-empty string if present");
    }
    // Decision 79: its money is the player's unpaid win, so a malformed one refuses the boot like any other money field.
    if (obj.pendingRound !== undefined) {
      const pending = obj.pendingRound as unknown as Partial<Record<string, unknown>> | null;
      const whole = (value: unknown): boolean => typeof value === "string" && /^\d+$/.test(value);
      if (
        typeof pending !== "object" ||
        pending === null ||
        typeof pending.roundIdentifier !== "string" ||
        pending.roundIdentifier.length === 0 ||
        (pending.state !== "awaiting_presentation" && pending.state !== "settling") ||
        !whole(pending.unpaidWinMinor) ||
        !whole(pending.betAmountMinor)
      ) {
        throw new BootDataError(`GameInitialSettingsLoader: invalid pendingRound ${JSON.stringify(obj.pendingRound)} (expected roundIdentifier, state awaiting_presentation|settling, unpaidWinMinor and betAmountMinor in whole minor units)`);
      }
    }

    // ── Big-win tier breakpoints validation ──────────────────
    if (obj.bigWinTiers !== undefined) {
      if (!Array.isArray(obj.bigWinTiers)) {
        throw new BootDataError("GameInitialSettingsLoader: bigWinTiers must be an array");
      }
      for (const tier of obj.bigWinTiers) {
        if (
          !tier ||
          typeof tier.levelIdentifier !== "string" ||
          tier.levelIdentifier.length === 0 ||
          typeof tier.minWinMultiplier !== "number" ||
          !Number.isFinite(tier.minWinMultiplier) ||
          tier.minWinMultiplier < 0
        ) {
          throw new BootDataError("GameInitialSettingsLoader: invalid entry in bigWinTiers");
        }
      }
    }
  }
}